import React, { useState, useEffect } from 'react';
import { shopifyResize } from './previewImage';

// Renders a product image with a white background by default.
// Only applies a grey background when the actual product pixels are
// predominantly white/near-white (so the product is visible against it).
// Transparent background pixels are excluded from the analysis so that
// a dark product on a transparent background is not falsely flagged.
//
// `size`: when given, requests a CDN-resized Shopify variant (e.g. _400x.jpg)
// instead of the full-resolution original. Non-Shopify URLs pass through.
// Critical for bulk print pages that render hundreds of thumbnails.
//
// The rendered <img> intentionally does NOT set crossOrigin: many of the
// same URLs are also fetched without crossOrigin elsewhere on the page
// (bundle preview swatches, plain <img> tags). The browser keys its image
// cache by URL+CORS-mode — once an image is cached as non-CORS, a later
// CORS request gets naturalWidth=0 even though the server supports CORS.
// Pixel analysis happens via a separate probe Image() with a cache-busting
// query string, so it doesn't collide with rendered <img> cache entries.
export default function SmartProductImage({ src, alt = '', style, className, transparent = false, size = null }) {
    const resolvedSrc = size ? shopifyResize(src, size) : src;
    const [lightBg, setLightBg] = useState(false);
    useEffect(() => {
        if (transparent || !resolvedSrc) return;
        const probe = new Image();
        probe.crossOrigin = 'anonymous';
        probe.onload = () => {
            try {
                const canvas = document.createElement('canvas');
                canvas.width = 20; canvas.height = 20;
                const ctx = canvas.getContext('2d');
                ctx.drawImage(probe, 0, 0, 20, 20);
                const { data } = ctx.getImageData(0, 0, 20, 20);
                let product = 0, lightProduct = 0;
                for (let i = 0; i < data.length; i += 4) {
                    const [r, g, b, a] = [data[i], data[i+1], data[i+2], data[i+3]];
                    if (a < 30) continue;
                    product++;
                    if (r > 210 && g > 210 && b > 210) lightProduct++;
                }
                if (product > 0 && lightProduct / product > 0.90) setLightBg(true);
            } catch (_) { /* CORS taint or no CORS — skip */ }
        };
        probe.onerror = () => { /* probe failure is non-fatal */ };
        probe.src = resolvedSrc + (resolvedSrc.includes('?') ? '&' : '?') + '__corsprobe=1';
    }, [resolvedSrc, transparent]);
    return (
        <img
            src={resolvedSrc}
            alt={alt}
            style={{ ...style, backgroundColor: transparent ? 'transparent' : (lightBg ? '#9ca3af' : '#fff') }}
            className={className}
        />
    );
}
